import React, { useEffect, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { enumChecker } from '@soundxyz/utils/constants/enums';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { BondingCurveSaleList } from '@components/Feed/ListViews/ReleaseFeed/BondingCurveSale';
import { BondingCurveSoldList } from '@components/Feed/ListViews/ReleaseFeed/BondingCurveSold';
import { PrimarySaleList } from '@components/Feed/ListViews/ReleaseFeed/PrimarySale';
import { ReleaseDroppedList } from '@components/Feed/ListViews/ReleaseFeed/ReleaseDropped';
import { ReleaseLikedList } from '@components/Feed/ListViews/ReleaseFeed/ReleaseLiked';
import { ReleasePlaylistAddedList } from '@components/Feed/ListViews/ReleaseFeed/ReleasePlaylistAdded';
import { ReleasesAddedList } from '@components/Feed/ListViews/ReleaseFeed/ReleasesAdded';
import { SecondarySaleList } from '@components/Feed/ListViews/ReleaseFeed/SecondarySale';
import { EmptyState } from '@components/Feed/ListViews/components/EmptyState';
import { Refresh } from '@components/Feed/ListViews/components/Refresh';
import { FeedListSkeleton } from '@components/Feed/ListViews/components/Skeleton';
import { FeedError } from '@components/Feed/Placeholders';
import { ReleaseActivityFeedTypeFilterOption } from '@graphql/reactQuery';
import { usePopState } from '@hooks/usePopState';
import { TEMPORARY_SUCCESS_STATE_MS } from '@utils/constants';
import { FeedHeader } from './FeedHeader';
import type { Activity } from './store';
import { ReleaseActivityState, useReleaseActivityState } from './store';
import { useReleaseActivityFeed } from './useReleaseActivityFeed';

export const ActionComponent = ({ item }: { item: Activity }) => {
  switch (item.__typename) {
    case 'PrimarySale':
      return <PrimarySaleList key={item.id} activityFrag={item} />;

    case 'SecondarySale':
      return <SecondarySaleList key={item.id} activityFrag={item} />;

    case 'BondingCurveSale':
      return <BondingCurveSaleList key={item.id} activityFrag={item} />;

    case 'BondingCurveSold':
      return <BondingCurveSoldList key={item.id} activityFrag={item} />;

    case 'ReleasesAddedToPlaylist':
      return item.playlist ? <ReleasesAddedList key={item.id} activityFrag={item} /> : null;

    case 'ReleasesAddedRemovedFromPlaylist':
      return item.playlist ? (
        <ReleasePlaylistAddedList
          userFrag={item.user}
          isoDateString={item.date}
          playlistFrag={item.playlist}
        />
      ) : null;

    case 'PlaylistCreated':
      return item.playlist ? (
        <ReleasePlaylistAddedList
          userFrag={item.user}
          playlistFrag={item.playlist}
          isoDateString={item.date}
        />
      ) : null;

    case 'ReleaseDropped':
      return <ReleaseDroppedList key={item.id} activityFrag={item} />;

    case 'ReleaseLiked':
      return item.release ? <ReleaseLikedList key={item.id} activityFrag={item} /> : null;

    default:
      return null;
  }
};

export const renderItemContent = ({
  item,
}: {
  index: number;
  item: Activity;
  releaseId: string;
}) => <ActionComponent key={item.id} item={item} />;

export const isReleaseActivityFeedTypeFilterOption = enumChecker(
  ReleaseActivityFeedTypeFilterOption,
);

export function ReleaseActivityFeed({ releaseId }: { releaseId: string }) {
  const [isFetchingNewActivity, setIsFetchingNewActivity] = useState(false);
  const [showRefreshSuccess, setShowRefreshSuccess] = useState(false);
  const { data, hasNextPage, loadMore, refetch, loading, setFilter, error } =
    useReleaseActivityFeed({
      releaseId,
    });

  const snapshot = useReleaseActivityState();

  const { isPopStateEvent } = usePopState();

  const memoizedFooter = useMemo(() => {
    if (!loading && !isFetchingNewActivity && data.length === 0) return <EmptyState />;
    if (!loading && !isFetchingNewActivity && !hasNextPage) return null;
    return <FeedListSkeleton />;
  }, [data.length, hasNextPage, isFetchingNewActivity, loading]);

  const refetchActivity = useStableCallback(async () => {
    setIsFetchingNewActivity(true);
    await refetch();

    // provide visual feedback for refetch
    await new Promise(resolve => setTimeout(resolve, 200));

    setShowRefreshSuccess(true);
    setIsFetchingNewActivity(false);
  });

  // reset the refresh success state after a timeout
  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowRefreshSuccess(false);
    }, TEMPORARY_SUCCESS_STATE_MS);
    return () => clearTimeout(timeout);
  }, [showRefreshSuccess]);

  const snapshotFilterType = snapshot.filter[releaseId]?.types;
  const memoizedReleaseActivityFeedType = useMemo(() => {
    return snapshotFilterType?.filter(isReleaseActivityFeedTypeFilterOption) || [];
  }, [snapshotFilterType]);

  const showError = !!error && data.length === 0 && !loading;

  return (
    <>
      <FeedHeader
        filter={memoizedReleaseActivityFeedType}
        setFilter={setFilter}
        icons={
          <Refresh
            isFetchingNewActivity={isFetchingNewActivity}
            refetch={refetchActivity}
            showRefreshSuccess={showRefreshSuccess}
          />
        }
      />

      {showError && <FeedError divider />}

      <div className="overflow-hidden">
        <Virtuoso
          useWindowScroll
          data={data}
          itemContent={(index, item) => renderItemContent({ index, item, releaseId })}
          endReached={loadMore}
          rangeChanged={({ startIndex }) =>
            (ReleaseActivityState.scrollIndices[releaseId] = startIndex)
          }
          components={{
            Footer: () => memoizedFooter,
          }}
          overscan={100}
          // Scroll to global state scroll index if popstate event (user used back or forward button in browser)
          // Used to avoid scroll jumping when user switches between tabs
          // Virtuoso breaks if we pass undefined, so we pass an empty object instead
          {...(isPopStateEvent && snapshot.scrollIndices[releaseId]
            ? {
                initialTopMostItemIndex: snapshot.scrollIndices[releaseId] || 0,
              }
            : {})}
        />
      </div>
    </>
  );
}
